import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { CONFIGURACION_TABS } from '@/config/configuracion';

/**
 * Interfaz para un Tab individual
 */
export interface Tab {
    /** ID único del tab (generado automáticamente) */
    id: string;
    /** Título mostrado en el tab (del MenuItem.nombre) */
    title: string;
    /** Ruta completa de navegación */
    route: string;
    /** Nombre del ícono de Lucide React (del MenuItem.icono) */
    icon?: string;
    /** Si el tab puede ser cerrado */
    closeable: boolean;
    /** Si el tab está anclado (no cuenta para límite) */
    isPinned: boolean;
    /** Metadata adicional del tab */
    metadata?: Record<string, any>;
}

/**
 * Resultado de operación openTab
 */
export interface OpenTabResult {
    /** Si la operación fue exitosa */
    success: boolean;
    /** Razón del resultado */
    reason?: 'created' | 'exists' | 'limit';
}

/**
 * Estado global de Tabs
 */
interface TabsState {
    /** Lista de tabs abiertos */
    tabs: Tab[];
    /** ID del tab actualmente activo */
    activeTabId: string | null;
    /** Número máximo de tabs permitidos (configurable) */
    maxTabs: number;

    // ========== ACTIONS ==========

    /**
     * Abre un nuevo tab o activa uno existente
     * @returns Resultado con success y reason
     */
    openTab: (tab: Omit<Tab, 'id' | 'isPinned'>) => OpenTabResult;

    /**
     * Cierra un tab por ID
     * @returns true si no quedan tabs closeables (para navegar a dashboard)
     */
    closeTab: (tabId: string) => boolean;

    /**
     * Cierra todos los tabs (excepto non-closeable)
     */
    closeAllTabs: () => void;

    /**
     * Cierra todos los tabs excepto el especificado
     */
    closeOtherTabs: (tabId: string) => void;

    /**
     * Establece el tab activo
     */
    setActiveTab: (tabId: string) => void;

    /**
     * Ancla un tab (no cuenta para límite)
     */
    pinTab: (tabId: string) => void;

    /**
     * Desancla un tab
     */
    unpinTab: (tabId: string) => void;

    /**
     * Reordena tabs (drag & drop)
     */
    reorderTabs: (fromIndex: number, toIndex: number) => void;

    /**
     * Establece el límite máximo de tabs
     */
    setMaxTabs: (max: number) => void;

    // ========== UTILITIES ==========

    /**
     * Busca un tab por su ruta
     */
    getTabByRoute: (route: string) => Tab | undefined;

    /**
     * Verifica si se puede abrir un nuevo tab
     */
    canOpenNewTab: () => boolean;
}

/**
 * Store de Tabs - Gestión de pestañas multi-ventana
 * 
 * Características:
 * - Prevención de duplicados (activa tab existente)
 * - Límite configurable (default: 10)
 * - Tabs anclados no cuentan para límite
 * - Persistencia en localStorage
 * - Auto-activación del siguiente tab al cerrar
 */
export const useTabsStore = create<TabsState>()(
    persist(
        (set, get) => ({
            tabs: [],
            activeTabId: null,
            maxTabs: CONFIGURACION_TABS.MAX_TABS,  // Desde config centralizada

            // ========== OPEN TAB ==========
            openTab: (tabData) => {
                const state = get();

                // 1. Verificar si tab ya existe por ruta
                const existingTab = state.tabs.find((t) => t.route === tabData.route);
                if (existingTab) {
                    // Activar tab existente
                    set({ activeTabId: existingTab.id });
                    return { success: true, reason: 'exists' };
                }

                // 2. Verificar límite de tabs (solo cuentan no-pinned)
                const unpinnedCount = state.tabs.filter((t) => !t.isPinned).length;
                if (unpinnedCount >= state.maxTabs) {
                    return { success: false, reason: 'limit' };
                }

                // 3. Crear nuevo tab
                const newTab: Tab = {
                    id: `tab-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
                    ...tabData,
                    isPinned: false,
                };

                set({
                    tabs: [...state.tabs, newTab],
                    activeTabId: newTab.id,
                });

                return { success: true, reason: 'created' };
            },

            // ========== CLOSE TAB ==========
            closeTab: (tabId) => {
                const state = get();
                const tab = state.tabs.find((t) => t.id === tabId);

                // No cerrar tabs non-closeable
                if (tab && !tab.closeable) return false;

                const newTabs = state.tabs.filter((t) => t.id !== tabId);

                // Si cerramos el tab activo, activar siguiente/previo
                let newActiveId = state.activeTabId;
                if (state.activeTabId === tabId) {
                    const closedIndex = state.tabs.findIndex((t) => t.id === tabId);
                    // Intentar siguiente, luego previo
                    const nextTab = newTabs[closedIndex] || newTabs[closedIndex - 1];
                    newActiveId = nextTab?.id || null;
                }

                set({
                    tabs: newTabs,
                    activeTabId: newActiveId,
                });

                // Si no quedan tabs closeables, retornar true para navegar a dashboard
                const hasCloseableTabs = newTabs.some(t => t.closeable);
                return !hasCloseableTabs;
            },

            // ========== CLOSE ALL TABS ==========
            closeAllTabs: () => {
                set((state) => ({
                    tabs: state.tabs.filter((t) => !t.closeable),
                    activeTabId: null,
                }));
            },

            // ========== CLOSE OTHER TABS ==========
            closeOtherTabs: (tabId) => {
                set((state) => ({
                    tabs: state.tabs.filter((t) => t.id === tabId || !t.closeable),
                    activeTabId: tabId,
                }));
            },

            // ========== SET ACTIVE TAB ==========
            setActiveTab: (tabId) => {
                set({ activeTabId: tabId });
            },

            // ========== PIN TAB ==========
            pinTab: (tabId) => {
                set((state) => ({
                    tabs: state.tabs.map((t) =>
                        t.id === tabId ? { ...t, isPinned: true } : t
                    ),
                }));
            },

            // ========== UNPIN TAB ==========
            unpinTab: (tabId) => {
                set((state) => ({
                    tabs: state.tabs.map((t) =>
                        t.id === tabId ? { ...t, isPinned: false } : t
                    ),
                }));
            },

            // ========== REORDER TABS ==========
            reorderTabs: (fromIndex, toIndex) => {
                set((state) => {
                    const newTabs = [...state.tabs];
                    const [removed] = newTabs.splice(fromIndex, 1);
                    newTabs.splice(toIndex, 0, removed);
                    return { tabs: newTabs };
                });
            },

            // ========== SET MAX TABS ==========
            setMaxTabs: (max) => {
                set({ maxTabs: Math.max(1, Math.min(50, max)) }); // Clamp 1-50
            },

            // ========== GET TAB BY ROUTE ==========
            getTabByRoute: (route) => {
                return get().tabs.find((t) => t.route === route);
            },

            // ========== CAN OPEN NEW TAB ==========
            canOpenNewTab: () => {
                const state = get();
                const unpinnedCount = state.tabs.filter((t) => !t.isPinned).length;
                return unpinnedCount < state.maxTabs;
            },
        }),
        {
            name: 'tabs-storage',
            // Solo persistir lo necesario
            partialize: (state) => ({
                tabs: state.tabs,
                activeTabId: state.activeTabId,
                maxTabs: state.maxTabs,
            }),
        }
    )
);
